/**
 * ============================================================================
 * USE TWO FACTOR HOOK
 * ============================================================================
 * 
 * Custom hook for two-factor authentication
 */

import { useState, useCallback } from 'react';
import * as twoFactorService from '../services/twoFactor';

export const useTwoFactor = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [status, setStatus] = useState(null);
  const [secret, setSecret] = useState(null);
  const [backupCodes, setBackupCodes] = useState([]);

  /**
   * Generate 2FA secret
   */
  const generateSecret = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await twoFactorService.generate2FASecret();
      setSecret(response.data);
      setBackupCodes(response.data?.backupCodes || []);
      return response.data;
    } catch (err) {
      setError(err.message || 'Failed to generate 2FA secret');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Verify 2FA setup
   */
  const verifySetup = useCallback(async (code) => {
    try {
      setLoading(true);
      setError(null);
      const response = await twoFactorService.verify2FASetup(code);
      return response.data;
    } catch (err) {
      setError(err.message || '2FA setup verification failed');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Enable 2FA
   */
  const enable2FA = useCallback(async (code) => {
    try {
      setLoading(true);
      setError(null);
      const response = await twoFactorService.enable2FA(code);
      await loadStatus(); // Refresh status
      return response.data;
    } catch (err) {
      setError(err.message || 'Failed to enable 2FA');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Disable 2FA
   */
  const disable2FA = useCallback(async (code, password) => {
    try {
      setLoading(true);
      setError(null);
      const response = await twoFactorService.disable2FA(code, password);
      await loadStatus(); // Refresh status
      return response.data;
    } catch (err) {
      setError(err.message || 'Failed to disable 2FA');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Verify 2FA login
   */
  const verifyLogin = useCallback(async (code) => {
    try {
      setLoading(true);
      setError(null);
      const response = await twoFactorService.verify2FALogin(code);
      return response.data;
    } catch (err) {
      setError(err.message || '2FA verification failed');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Get backup codes
   */
  const loadBackupCodes = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const codes = await twoFactorService.getBackupCodes();
      setBackupCodes(codes);
      return codes;
    } catch (err) {
      setError(err.message || 'Failed to load backup codes');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Regenerate backup codes
   */
  const regenerateBackupCodes = useCallback(async (code) => {
    try {
      setLoading(true);
      setError(null);
      const codes = await twoFactorService.regenerateBackupCodes(code);
      setBackupCodes(codes);
      return codes;
    } catch (err) {
      setError(err.message || 'Failed to regenerate backup codes');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Check if 2FA is enabled
   */
  const checkEnabled = useCallback(async () => {
    try {
      const enabled = await twoFactorService.is2FAEnabled();
      return enabled;
    } catch (err) {
      console.error('Failed to check 2FA status:', err);
      return false;
    }
  }, []);

  /**
   * Get 2FA status (graceful on 404 / missing backend)
   */
  const loadStatus = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await twoFactorService.get2FAStatus();
      const data = response?.data ?? response;
      setStatus(data ?? null);
      return data ?? null;
    } catch (err) {
      setStatus(null);
      setError(err?.message || 'Failed to load 2FA status');
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Verify backup code
   */
  const verifyBackupCode = useCallback(async (backupCode) => {
    try {
      setLoading(true);
      setError(null);
      const valid = await twoFactorService.verifyBackupCode(backupCode);
      return valid;
    } catch (err) {
      setError(err.message || 'Backup code verification failed');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Request 2FA recovery
   */
  const requestRecovery = useCallback(async (email, phone) => {
    try {
      setLoading(true);
      setError(null);
      const response = await twoFactorService.request2FARecovery(email, phone);
      return response.data;
    } catch (err) {
      setError(err.message || 'Failed to request 2FA recovery');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Complete 2FA recovery
   */
  const completeRecovery = useCallback(async (recoveryToken, newCode) => {
    try {
      setLoading(true);
      setError(null);
      const response = await twoFactorService.complete2FARecovery(recoveryToken, newCode);
      await loadStatus(); // Refresh status
      return response.data;
    } catch (err) {
      setError(err.message || 'Failed to complete 2FA recovery');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [loadStatus]);

  return {
    // State
    status,
    secret,
    backupCodes,
    loading,
    error,
    
    // Actions
    generateSecret,
    verifySetup,
    enable2FA,
    disable2FA,
    verifyLogin,
    loadBackupCodes,
    regenerateBackupCodes,
    checkEnabled,
    loadStatus,
    verifyBackupCode,
    requestRecovery,
    completeRecovery,
  };
};

export default useTwoFactor;
